/*
 * Size Recommender — tuning config (deterministic, client-side).
 *
 * EVERY number below is a FIRST-PASS, TUNABLE default. Nothing here is sacred.
 * Calibrate against real return data later: the entire model lives in this one
 * file, so re-tuning is a single synced edit across all four themes.
 *
 * Model overview (see assets/size-recommender.js):
 *   1. Estimate the shopper's BODY circumference for each part from
 *      height(cm), weight(kg), age and sex (a robust linear anthropometric model).
 *   2. The size_guide charts store GARMENT FLAT half-measurements, so
 *      garment circumference = chartValue * flatToCirc (= 2).
 *   3. ease = garment_circ - body_circ  (cm of room).
 *      ease < tight  -> "Too tight"
 *      tight..loose  -> "Perfect fit"
 *      ease > loose   -> "Too loose"
 *   4. Pick the size minimising a weighted squared deviation from `ideal` ease.
 *
 * Body parts handled as circumferences: chest (a.k.a. bust), waist, hip, thigh.
 * shoulder / sleeve / length columns are NOT circumferences and are never
 * doubled or fit-scored — they only feed the optional length note.
 */
(function () {
  var CONFIG = {
    version: 2,

    // chart flat half-measurement -> body circumference scale
    flatToCirc: 2,

    // Which body parts to ALWAYS evaluate per garment group (matches the wizard
    // steps + result fit-lines). Tops show chest/waist/hip even when the chart
    // only has a chest column (boxy-knit assumption, see fallbackWidthFrom).
    evaluateParts: {
      top: ['chest', 'waist', 'hip'],
      dress: ['chest', 'waist', 'hip'],
      bottom: ['waist', 'hip', 'thigh']
    },
    // When a part has no chart column, borrow another column's garment width
    // (a sweater is a tube: its chest width also governs the waist/hip drape).
    fallbackWidthFrom: {
      top: { waist: 'chest', hip: 'chest' },
      dress: { waist: 'chest', hip: 'chest' },
      bottom: { thigh: 'hip', waist: 'hip', hip: 'waist' }
    },

    // sane input ranges (used for validation)
    inputBounds: {
      height: { min: 120, max: 220 }, // cm
      weight: { min: 30, max: 200 }   // kg
    },

    // tie-break when two sizes score equally: "up" = prefer the larger size
    // (cashmere is forgiving slightly loose), "down" = prefer the smaller.
    tieBreak: 'up',

    // Product-cut adjustment. The brand body chart (heightWeightChart, below)
    // gives the CENTRAL size from height+weight; the SPECIFIC product's garment
    // chart may then nudge that result up (unusually slim/small-cut styles) or
    // down (boxy/oversized styles) by at most this many size-steps — and only
    // when the central size's primary-part ease falls outside the wearable band.
    // Regular cuts stay on the body-chart size, so the brand anchors hold.
    // 0 = pure body chart (ignore the product's own measurements). TUNABLE.
    cutClampSteps: 1,

    // Body-shape adjustment. After height/weight set the central brand-chart size,
    // the shopper's Build (shoulder/chest width) and Stomach (midsection) answers
    // nudge the size ordinal by these amounts (added to the chart target before
    // snapping). 'average' = 0 on both, so the anchors hold; the extremes shift
    // the result up/down (combined max ~+/-1 size). TUNABLE.
    bodyShape: {
      build:   { narrow: -0.5, average: 0, broad: 0.5 },
      stomach: { flat: -0.4, average: 0, round: 0.4 }
    },

    // How close to the midpoint between two available sizes counts as "borderline"
    // (fractional position within 0.5 +/- this). At the edge, the result names BOTH
    // sizes + which we suggest, instead of one hard letter. 0 disables. TUNABLE.
    boundaryBand: 0.15,

    // ===== Fit standard from the SKU (A / D) ================================
    // The letter that closes the size block of a variant SKU names the standard
    // the garment was cut on: "KW060291-1481S0000-004A" = A, "...-004D" = D.
    // A and D are the same shape; D is cut about 2 cm wider across the chest
    // (the A/D size charts agree: +2..3 cm chest, +1.5 cm length). The body
    // chart below is calibrated on the D/E block, so D stays put and a size cut
    // on A leans UP by `shift.A` size-steps: 0.25 = a shopper in the top quarter
    // of a size band takes the next size on A. Deliberately far below the
    // geometric 2/3 (2 cm of a 3 cm grade): shoppers buy the same label on A
    // charts as on D charts and keep it about as often (2025-10..2026-09
    // returns: A leans "too small", D and E do not). TUNABLE.
    // A product's chart follows its own code ("wk81a" lists A measurements,
    // "wk81" / "wk81d" list D). When a size's SKU letter and that chart
    // disagree, the chart's widths are corrected by `gapCm` for that size.
    fitStandard: {
      shift: { A: 0.25, D: 0 },
      gapCm: 2 // D minus A, flat width
    },

    // ===== Per-product corrections from returns ============================
    // key = the product's size-guide code (tag uniqueTag-<CODE>, lower-case),
    // the same on all four stores. shift = size-steps added to the body-chart
    // target: + = the style runs small (lean up), - = runs large (lean down).
    // Listed = "too small" / "too big" returns well above the catalogue rate
    // (5.3% / 3.1% of units) that the chart does not make the engine act on.
    // A correction that moves the size is final; the garment guard
    // (cutClampSteps) only acts when it did not. Styles whose chart already
    // makes the guard move most bodies are not listed, the guard covers them.
    // cmPerSize = one size step in flat chart width; it turns a shift into the
    // chart correction the measured-body ("Use my own measurements") path uses.
    // The productFit list is GENERATED by
    // size-recommender-ad-2026-10-02/make_overrides.py from 19,441 units /
    // 4,216 returns (orders 2025-10-01..2026-10-02); re-run it to re-tune
    // instead of editing by hand.
    cmPerSize: 3,
    productFit: {
      mc0070: { shift:  0.30 }, // 14 sold: 7 too small, 0 too big
      wc46:   { shift:  0.30 }, // 49 sold: 9 too small, 1 too big
      wk0968: { shift:  0.30 }, // 24 sold: 6 too small, 1 too big
      wk1021: { shift:  0.25 }, // 263 sold: 32 too small, 6 too big
      wk1073: { shift:  0.25 }, // 77 sold: 10 too small, 1 too big
      wk01d:  { shift:  0.20 }, // 69 sold: 10 too small, 2 too big
      wk01:   { shift:  0.15 }, // 1202 sold: 93 too small, 23 too big
      wk1065: { shift:  0.15 }, // 101 sold: 12 too small, 3 too big
      wk1068: { shift:  0.15 }, // 74 sold: 10 too small, 3 too big
      wk864:  { shift: -0.15 }, // 111 sold: 6 too small, 9 too big
      wk81d:  { shift: -0.25 }, // 64 sold: 2 too small, 7 too big
      wk1085: { shift: -0.30 }, // 171 sold: 2 too small, 13 too big
      wk1095: { shift: -0.30 }, // 41 sold: 1 too small, 6 too big
      wk850:  { shift: -0.40 }, // 59 sold: 0 too small, 8 too big
      wt0001: { shift: -0.50 }  // 25 sold: 0 too small, 10 too big
    },

    // half-width (cm) of the "Perfect fit" window around the IDEAL ease, used
    // for the per-part verdict shown on the body figure. Smaller => adjacent
    // sizes differentiate (bigger = loose, smaller = tight). Scoring/ranking is
    // unaffected (still minimises distance to ideal). TUNABLE.
    verdictWindow: 3,

    // women's knitwear is worn more fitted than men's -> shift the TARGET ease
    // (centering only; verdict thresholds are unchanged) per sex. TUNABLE.
    easeAdjustBySex: { female: -4, male: 0 },

    // ===== Body size chart: height(cm) + weight(kg) -> size ==================
    // THE central mapping. WEIGHT drives the size (it governs body girth, which
    // is what size labels track); HEIGHT only applies a small, CORRECTLY-SIGNED
    // correction: at the same weight a taller body is leaner -> a slightly
    // SMALLER size (this mirrors the anthropometric model's negative height
    // coefficient and reproduces the brand anchors 180cm/80kg -> men L, women XL).
    // The previous build blended a height interpolation with the WRONG sign
    // (taller -> bigger) at 30% weight, which oversized tall shoppers by up to
    // ~3 size-steps (e.g. a 180/80 woman came out 2XL); that height term is gone.
    // We analysed all 1,443 live size_guide charts: garment chest width per size
    // varies ±1-2 sizes BETWEEN products, so a single product's garment chart
    // can't tell true body size — but it CAN say whether THAT style runs slim or
    // boxy, which is applied as the bounded cutClampSteps nudge (see above).
    //   female = the brand's published women's chart (weight ranges authoritative).
    //   male   = standard cashmere men's sizing — TUNABLE; replace with the
    //            brand's official men's chart when available.
    // Set heightWeightChart: null to fall back to the garment-ease model.
    heightWeightChart: {
      // size-steps of change per cm of height away from the chart's mean height,
      // applied NEGATIVELY (taller -> smaller). Keeps the model BMI-aware without
      // letting height override weight. 0 = pure weight. TUNABLE.
      heightCoef: 0.02,
      // Per-sex nudge added to the interpolated weight target before snapping.
      // female 0.05 (published women's chart reads a touch tight); male 0.15
      // (men's chart is a placeholder that reads small). If a sex's chart is ever
      // corrected, re-zero that bias so the fix and the nudge don't stack. TUNABLE.
      sizeBias: { female: 0.05, male: 0.15 },
      // Gate BELOW-chart extrapolation: dropping under the smallest chart row
      // must be EARNED by the body, not granted on any weight shortfall.
      // Weight alone used to extrapolate any sub-chart-weight body down to XS,
      // but garment length & sleeves shrink with the size: a shopper merely a
      // couple of kg under the smallest row must stay on that row — dropping
      // below trades girth ease they don't need for a length they can't wear.
      // Calibrated from return IN74363 (female 166cm/50kg -> XS "too small";
      // now S). The drop below the smallest row fades in on EITHER axis,
      // whichever is stronger: height under the smallest row's height midpoint
      // (petite frames, e.g. 155cm/45kg, still reach XS unchanged), or weight
      // a full chart-step under the smallest row's published low bound (so a
      // genuinely sub-chart body like 165cm/40kg also reaches XS instead of
      // being pinned to S against the fit model). true/false. TUNABLE.
      belowChartHeightGuard: true,
      female: [
        { size: 'S',   h: [156, 165], w: [52, 60] },
        { size: 'M',   h: [159, 168], w: [57, 66] },
        { size: 'L',   h: [162, 170], w: [63, 73] },
        { size: 'XL',  h: [165, 173], w: [70, 82] },
        { size: '2XL', h: [167, 176], w: [80, 92] }
      ],
      male: [
        { size: 'S',   h: [165, 173], w: [60, 69] },
        { size: 'M',   h: [170, 178], w: [66, 76] },
        { size: 'L',   h: [175, 183], w: [74, 86] },
        { size: 'XL',  h: [180, 188], w: [84, 97] },
        { size: '2XL', h: [184, 192], w: [95, 110] }
      ]
    },

    // ----- Anthropometric body-circumference model (cm) -----------------
    // Used for the MANUAL-measurement path (and as a fallback when no
    // heightWeightChart applies). circ_body(part) = base + wCoef*weight + hCoef*(height - refH)
    // Height + weight only. WEIGHT is the dominant driver; HEIGHT carries a
    // small NEGATIVE coefficient because, at the same weight, a taller person
    // is leaner (the mass is spread over more length) -> smaller girth. This
    // makes the model BMI-aware (short+heavy => larger, tall+light => smaller).
    // base = circumference at refH and 0 kg (extrapolated intercept). TUNABLE.
    anthro: {
      female: {
        refH: 165,
        chest: { base: 48, wCoef: 0.67, hCoef: -0.20 },
        waist: { base: 20, wCoef: 0.87, hCoef: -0.15 },
        hip:   { base: 55, wCoef: 0.67, hCoef: -0.18 },
        thigh: { base: 26, wCoef: 0.47, hCoef: -0.08 }
      },
      male: {
        refH: 178,
        chest: { base: 52, wCoef: 0.60, hCoef: -0.15 },
        waist: { base: 28, wCoef: 0.75, hCoef: -0.12 },
        hip:   { base: 66, wCoef: 0.40, hCoef: -0.12 },
        thigh: { base: 24, wCoef: 0.40, hCoef: -0.06 }
      }
    },

    // ----- Column-name synonyms (lowercased, trimmed) ------------------
    // chest/waist/hip/thigh are circumference parts (fit-scored).
    // shoulder/sleeve/length are reference-only.
    columnSynonyms: {
      chest:    ['chest width', 'bust width', 'chest', 'bust'],
      waist:    ['waist width', 'waist', 'belt width', 'belt'], // pants charts label the waistband as "Belt width"
      hip:      ['hip width', 'hips width', 'hip', 'hips'],
      thigh:    ['thigh width', 'thigh'],
      shoulder: ['shoulder width', 'shoulder'],
      sleeve:   ['sleeve length', 'sleeve width', 'sleeve'],
      length:   ['body length', 'back length', 'length']
    },

    // ----- Per garment-group ease bands + weights ----------------------
    // ease in cm of TOTAL circumference room (garment - body).
    // `loose`/`tight` are classification thresholds; `ideal` is the target.
    // `weight` ranks each part's importance when scoring the whole garment.
    // garmentGroup is inferred from the chart's columns (NOT product.type).
    // NOTE: cashmere knit STRETCHES, so a small negative ease (garment slightly
    // smaller than the body) still wears fine -> `tight` thresholds are allowed
    // to go negative. On tops the chest is the binding fit point; hip/waist are
    // secondary (low weight, very forgiving). Calibrate from returns later.
    categories: {
      top: {
        chest: { ideal: 10, tight: -2,  loose: 24, weight: 1.00 },
        hip:   { ideal: 4,  tight: -18, loose: 28, weight: 0.20 },
        waist: { ideal: 8,  tight: -4,  loose: 28, weight: 0.40 }
      },
      dress: {
        chest: { ideal: 6, tight: -3, loose: 18, weight: 0.50 },
        waist: { ideal: 5, tight: -3, loose: 18, weight: 0.30 },
        hip:   { ideal: 6, tight: -4, loose: 20, weight: 0.30 }
      },
      bottom: {
        // knit/elastic waistbands stretch a lot -> waist is NOT the binding
        // fit point and must tolerate large negative ease; HIP drives the size.
        // waist + thigh are "soft" (drape/stretch) -> never falsely "loose".
        waist: { ideal: 0, tight: -28, loose: 10, weight: 0.10, soft: true },
        hip:   { ideal: 6, tight: -3,  loose: 14, weight: 0.70 },
        thigh: { ideal: 6, tight: -4,  loose: 26, weight: 0.20, soft: true }
      }
    }
  };

  // deep-freeze so nothing mutates it at runtime
  function freeze(obj) {
    Object.getOwnPropertyNames(obj).forEach(function (key) {
      var value = obj[key];
      if (value && typeof value === 'object') freeze(value);
    });
    return Object.freeze(obj);
  }

  window.SIZE_RECOMMENDER_CONFIG = freeze(CONFIG);
})();
